import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import feather from 'feather-icons';
import CryptoJS from 'crypto-js';
import { RootState } from '../../store';
import { ENV_CONFIG } from '../../shared/config/env.config';
import { useGetMenuOptionsQuery } from './Navbar.service';
import type { MenuItem, SubMenuItem } from './Navbar.model';
import {
  getAngularOrigin,
  primeAngularSession,
} from '../../shared/services/sessionBridge';
import {
  evalCondition,
  type ConditionContext,
} from '../../shared/utils/conditionEvaluator';
import './Navbar.scss';

const handleMenuEnter = (event: React.MouseEvent<HTMLElement>) => {
  const rect = event.currentTarget.getBoundingClientRect();
  document.documentElement.style.setProperty(
    '--submenu-top',
    `${rect.bottom + 10}px`
  );
};

// Angular EMIS origin per environment (LOCAL / STAGING / PRODUCTION),
// resolved by the shared session bridge.
const angularAppUrl = getAngularOrigin();

const Navbar: React.FC = () => {
  const s = useSelector((state: RootState) => state.session);
  const location = useLocation();
  const navigate = useNavigate();
  // Read from localStorage first
  const cachedMenu = localStorage.getItem('menuOptions');

  const { data: menuData, isLoading } = useGetMenuOptionsQuery(undefined, {
    // skip API call if already in localStorage
    skip: !!cachedMenu,
  });

  // Use cache if available, otherwise use fresh fetch
  const resolvedMenuData = cachedMenu ? JSON.parse(cachedMenu) : menuData;

  // Wait for the menu JSON before building the navbar. Until it arrives we
  // render an empty nav shell (no items) so the menu is only shown once the
  // data is available to be filtered by the conditions.
  const menuReady =
    resolvedMenuData !== undefined && resolvedMenuData !== null && !isLoading;
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const userTypeId = String(s.usertypeid ?? '');
  const userType1 = String(s.emisUsertype1 ?? '');
  const staffCheck = ['14', '8', '30'].includes(userTypeId);

  // Prime the Angular EMIS session over the bridge, then full-page redirect to
  // the Angular app. Path comes from the menu link; when absent, falls back to
  // the Angular home (/dashboard). Mirrors the tnemis navigateToAngular flow.
  const navigateToAngular = (link?: string): void => {
    const path =
      link && link.trim()
        ? link.startsWith('/')
          ? link
          : `/${link}`
        : '/dashboard';
    const targetUrl = `${angularAppUrl}${path}`;

    let redirectDone = false;
    const go = () => {
      if (redirectDone) return;
      redirectDone = true;
      window.location.href = targetUrl;
    };

    // Prime the session first, then redirect on completion; also redirect after
    // a short fallback so navigation is never blocked by the bridge.
    primeAngularSession().finally(go);
    setTimeout(go, 1500);
  };

  // ── middleware: mirrors Angular's canNavigate ──────────────────────────────
  const canNavigate = (link: string, website?: string): boolean => {
    if (website == 'emis') {
      navigateToAngular(link);
      return false; // abort React Router navigation
    }
    return true;
  };

  // Single handler used by every menu link click
  const handleLinkClick = (
    e: React.MouseEvent,
    link: string,
    website?: string,
    moduleName?: string
  ) => {
    setOpenIndex(null);
    // SLAS 2024 - 25: never navigate normally — always do the encrypted
    // redirect. Handled here so it applies to every render path (submenu,
    // mega-menu, and the "pink" block), not just one.
    if (handleSlasRedirect(moduleName)) {
      e.preventDefault(); // block Link's `to`/route from firing
      return;
    }
    if (!canNavigate(link, website)) {
      e.preventDefault(); // block Link's `to` from firing
    }
  };

  const menuItems: MenuItem[] = useMemo(() => {
    // The CloudFront app-menu JSON is a flat array of top-level menu items
    // (same list for all users; per-item visibility is handled by the
    // render-time `check(filtering_conditions)` calls).
    if (Array.isArray(resolvedMenuData)) return resolvedMenuData;

    // Fallback: legacy keyed API shape ({ status, data: { staff, school, ... } }).
    if (!resolvedMenuData?.status) return [];
    const emis = resolvedMenuData.data;

    if (['14', '8', '30'].includes(userTypeId)) return emis.staff;
    if (userTypeId === '28') return emis['model-school'];
    if (userTypeId === '31') return emis['data-entry'];
    if (userTypeId === '22') return emis.diet;
    if (userTypeId === '62' && userType1 === '1')
      return emis['staff-grievance'];
    return emis.school;
  }, [resolvedMenuData, userTypeId, userType1]);
  const emisReactDetails = JSON.parse(
    localStorage.getItem('emisReactDetails') || '{}'
  );
  const ctx: ConditionContext = {
    schooltypeId: String(s.schoolTypeId ?? ''),
    catty_id: String(s.catty_id ?? ''),
    userTypeId: String(s.usertypeid ?? ''),
    teacherType: String(s.teacherType ?? ''),
    subjectID: String(s.subject_id ?? ''),
    staffCheck: String(staffCheck),
    userName: String(s.username ?? ''),
    isKgbvSchool: String(emisReactDetails?.isKgbvSchool),
    isNscbavSchool: String(emisReactDetails?.isNscbavSchool),
    // Comes as 'Yes' / 'No'; used by conditions like `rteschooltype=Yes`.
    rteschooltype: String(emisReactDetails?.rteschooltype),
    allotedCheck: String(emisReactDetails?.allotedCheck),
    smardboardMenu: String(emisReactDetails?.smardboardMenu),
    taggedAs22: String(emisReactDetails?.taggedAs22),
    isTnsparkSchool: String(emisReactDetails?.isTnsparkSchool),
    staffViewVisible: String(emisReactDetails?.staffViewVisible),
    clsTeachMenu: String(emisReactDetails?.clsTeachMenu),
    teacherAssignCheck: String(emisReactDetails?.teacherAssignCheck),
  };

  const check = (condition?: string | null) => evalCondition(condition, ctx);
  const hasItems = (item: MenuItem) =>
    !!item.subMenus && item.subMenus.length > 0;

  // True when the given link matches the current route: exact match, or a
  // parent-of-current match (e.g. link "/staff" matches "/staff/list") without
  // over-matching (so "/staff" does not match "/staffroom", and "/" matches
  // nothing but itself).
  const pathMatches = (link?: string): boolean => {
    if (!link || link === '#' || link === '/') {
      return !!link && link === '/' && location.pathname === '/';
    }
    const cur = location.pathname;
    return cur === link || cur.startsWith(link + '/');
  };

  // A top-level menu option is "active" when its own link matches the current
  // route OR any of its sub-menu items matches. This is what highlights the
  // selected option (previously only an exact top-level link match highlighted,
  // so choosing a sub-option never lit its parent).
  const isMenuActive = (item: MenuItem): boolean => {
    if (pathMatches(item.link)) return true;
    for (const group of item.subMenus ?? []) {
      for (const sub of group.subMenuItems ?? []) {
        if (pathMatches(sub.link)) return true;
      }
    }
    return false;
  };
  const shouldInlineSubmenu = (item: MenuItem) =>
    (item.subMenus ?? []).every((subMenu) =>
      subMenu.subMenuItems.every(
        (subItem) => subItem.isMegaMenu === false && !!subItem.link
      )
    );

  useEffect(() => {
    feather.replace();
  }, [menuItems, openIndex]);
  // useEffect(() => {
  //   setOpenIndex(null);
  // }, [location.pathname]);

  const toggleSubmenu = (ind: number) => {
    setOpenIndex((prev) => (prev === ind ? null : ind));
  };

  const handleMenuClick = (clickText: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('handleMenuClick', clickText);
  };

  // Redirects the SLAS 2024-25 menu item to the encrypted SLAS site instead of
  // performing normal menu navigation. Mirrors the Angular handleSlasRedirect:
  // the username is AES-encrypted with the shared secret and appended to the
  // per-environment SLAS URL. Returns true when it handled a SLAS item (so the
  // caller suppresses default navigation); false for every other item.
  //
  // Field guard: React menu objects use `label` while the Angular payload uses
  // `module_name`. We accept whichever is present, and match on the module name
  // only (SLAS 2024 - 25) so the redirect fires regardless of the url/link value.
  const handleSlasRedirect = (moduleName?: string): boolean => {
    if (moduleName === 'SLAS 2024 - 25') {
      const userName = String(s.username ?? '');
      // Shared secret with the SLAS system — must match exactly on both sides.
      const encryptedUrNme = CryptoJS.AES.encrypt(
        userName,
        'SecretKeyEmis@2025'
      );
      const link = ENV_CONFIG.slasSiteURL + 1 + encryptedUrNme;
      window.open(link, '_blank', 'noopener,noreferrer');
      // window.location.href = link;
      return true;
    }
    return false;
  };

  // ── render helpers ─────────────────────────────────────────────────────────

  const renderSubMenuItem = (subItem: SubMenuItem, key: number) => {
    if (!check(subItem.filtering_conditions)) return null;

    if (subItem.isTitle) {
      return (
        <li className="category-heading" key={key}>
          {subItem.label} {subItem.website == 'emis-react' ? '(React)' : ''}
        </li>
      );
    }

    if (subItem.label === 'SLAS 2024 - 25') {
      return (
        <li className="nav-item" key={key}>
          <a
            href={subItem.link ?? '#'}
            className="nav-link"
            onClick={(e) => {
              // Block the default link/route, then run the encrypted SLAS
              // redirect. Only the SLAS redirect should happen for this item.
              e.preventDefault();
              handleSlasRedirect(subItem.module_name ?? subItem.label);
            }}
          >
            {subItem.label} {subItem.website == 'emis-react' ? '(React)' : ''}
          </a>
        </li>
      );
    }

    // if (subItem.label === 'Clubs') return null;

    if (subItem.label) {
      return (
        <li className="nav-item" key={key}>
          <Link
            className={`nav-link ${pathMatches(subItem.link) ? 'active' : ''}`}
            to={subItem.link ?? '#'}
            onClick={(e) =>
              handleLinkClick(
                e,
                subItem.link ?? '#',
                subItem.website,
                subItem.module_name ?? subItem.label
              )
            }
          >
            {subItem.label} {subItem.website == 'emis-react' ? '(React)' : ''}
          </Link>
          {subItem.badge && (
            <span className={`badge bg-${subItem.badge.variant}`}>
              {subItem.badge.text}
            </span>
          )}
        </li>
      );
    }

    return null;
  };

  const renderMegaSubMenuItem = (subItem: SubMenuItem, key: number) => {
    if (!check(subItem.filtering_conditions)) return null;

    if (subItem.isTitle) {
      return (
        <li className="category-heading" key={key}>
          {subItem.label} {subItem.website == 'emis-react' ? '(React)' : ''}
        </li>
      );
    }

    if (subItem.label && subItem.clickText) {
      return (
        <li key={key}>
          <button
            type="button"
            className="nav-link"
            onClick={(e) => handleMenuClick(subItem.clickText!, e)}
          >
            {subItem.label} {subItem.website == 'emis-react' ? '(React)' : ''}
          </button>
        </li>
      );
    }

    if (subItem.label) {
      return (
        <li className="nav-item" key={key}>
          <Link
            className={`nav-link ${pathMatches(subItem.link) ? 'active' : ''}`}
            to={subItem.link ?? '#'}
            onClick={(e) =>
              handleLinkClick(
                e,
                subItem.link ?? '#',
                subItem.website,
                subItem.module_name ?? subItem.label
              )
            }
          >
            {subItem.label} {subItem.website == 'emis-react' ? '(React)' : ''}
          </Link>
        </li>
      );
    }

    if (subItem.test) {
      return (
        <dl
          className="nav-item pink"
          style={{ color: 'var(--navbar-pink)' }}
          key={key}
        >
          <button type="button" className="nav-link pink">
            {subItem.labelTitle}
          </button>
          {subItem.test.map((data, dIdx) => (
            <li className="nav-item" style={{ marginLeft: 25 }} key={dIdx}>
              {data.clickText ? (
                <button
                  type="button"
                  className="nav-link"
                  onClick={(e) => handleMenuClick(data.clickText!, e)}
                >
                  {data.label}
                </button>
              ) : (
                <Link
                  className={`nav-link ${pathMatches(data.link) ? 'active' : ''}`}
                  to={data.link ?? '#'}
                  onClick={(e) =>
                    handleLinkClick(
                      e,
                      data.link ?? '#',
                      data.website,
                      data.module_name ?? data.label
                    )
                  }
                >
                  {data.label}
                </Link>
              )}
            </li>
          ))}
        </dl>
      );
    }

    return null;
  };

  const renderItem = (subItem: SubMenuItem, key: number) => {
    if (!check(subItem.filtering_conditions)) return null;
    if (subItem.isMegaMenu === false && subItem.link) {
      return (
        <li className="nav-item" key={key}>
          <Link
            className={`nav-link ${pathMatches(subItem.link) ? 'active' : ''}`}
            to={subItem.link ?? '#'}
            onClick={(e) =>
              handleLinkClick(
                e,
                subItem.link ?? '#',
                subItem.website,
                subItem.module_name ?? subItem.label
              )
            }
          >
            {subItem.label} {subItem.website == 'emis-react' ? '(React)' : ''}
          </Link>
          {subItem.badge && (
            <span className={`badge bg-${subItem.badge.variant}`}>
              {subItem.badge.text}
            </span>
          )}
        </li>
      );
    }
  };

  // ── JSX ────────────────────────────────────────────────────────────────────

  // Wait for the menu JSON: render an empty nav shell until it's loaded.
  if (!menuReady) {
    return (
      <nav className="bottom-navbar">
        <div className="fullscrollCls">
          <ul className="nav page-navigation" />
        </div>
      </nav>
    );
  }

  return (
    <nav className="bottom-navbar">
      <div className="fullscrollCls">
        <ul className="nav page-navigation">
          {menuItems.map((item, ind) => {
            if (!check(item.filtering_conditions)) return null;
            const is_active = isMenuActive(item);

            if (!item.isMegaMenu) {
              return (
                <li
                  key={item.id ?? ind}
                  className={`nav-item ${is_active ? 'active' : ''}`}
                >
                  {!hasItems(item) ? (
                    <Link
                      className="nav-link"
                      to={item.link ?? '#'}
                      onClick={(e) =>
                        handleLinkClick(
                          e,
                          item.link ?? '#',
                          item.website,
                          item.label
                        )
                      }
                    >
                      {item.icon && (
                        <i className="link-icon" data-feather={item.icon} />
                      )}
                      <span className="menu-title">{item.label}</span>
                    </Link>
                  ) : (
                    <button
                      type="button"
                      className="nav-link"
                      onClick={() => toggleSubmenu(ind)}
                    >
                      {item.icon && (
                        <i className="link-icon" data-feather={item.icon} />
                      )}
                      <span className="menu-title">{item.label}</span>
                      <i
                        className={`link-arrow ${openIndex === ind ? 'open' : ''}`}
                      />
                    </button>
                  )}

                  {hasItems(item) && (
                    <div
                      className={`submenu click-style${ind} ${openIndex === ind ? 'header-toggled' : ''}`}
                    >
                      {item.subMenus!.map((subMenu, sIdx) => (
                        <ul
                          className="submenu-item"
                          key={sIdx}
                          onMouseEnter={handleMenuEnter}
                        >
                          {subMenu.subMenuItems.map((subItem, siIdx) =>
                            renderSubMenuItem(subItem, siIdx)
                          )}
                        </ul>
                      ))}
                    </div>
                  )}
                </li>
              );
            }

            // Mega menu
            return (
              <li
                key={item.id ?? ind}
                className={`nav-item mega-menu ${is_active ? 'active' : ''}`}
              >
                <button
                  type="button"
                  className="nav-link"
                  onClick={() => toggleSubmenu(ind)}
                >
                  {item.icon && (
                    <i className="link-icon" data-feather={item.icon} />
                  )}
                  <span className="menu-title">{item.label}</span>
                  <i
                    className={`link-arrow ${openIndex === ind ? 'open' : ''}`}
                  />
                </button>
                <div
                  className={`click-style${ind} ${(item.subMenus?.length ?? 0) > 4 ? 'scrollCls' : ''} ${openIndex === ind ? 'header-toggled' : ''} ${shouldInlineSubmenu(item) ? 'inlineSubmenu' : 'submenu'}`}
                >
                  <div
                    className={shouldInlineSubmenu(item) ? 'row g-0' : 'row'}
                  >
                    {item.subMenus?.map((subMenu, sIdx) => {
                      if (!check(subMenu.filtering_conditions)) return null;

                      // Filter valid subItems first
                      const visibleItems = subMenu.subMenuItems.filter(
                        (subItem) =>
                          subItem.isMegaMenu === false && subItem.link
                            ? true // renderItem handles its own visibility
                            : true // renderMegaSubMenuItem too
                      );

                      // Don't render the col/ul at all if no items will show
                      const hasVisibleItems = subMenu.subMenuItems.some(
                        (subItem) => check(subItem.filtering_conditions)
                      );
                      if (!hasVisibleItems) return null;

                      return (
                        <div
                          className={
                            shouldInlineSubmenu(item)
                              ? 'col-12'
                              : 'col-md-3 mb-2'
                          }
                          key={sIdx}
                        >
                          <ul
                            className={
                              shouldInlineSubmenu(item)
                                ? 'submenu-item d-flex flex-wrap gap-3'
                                : 'submenu-item'
                            }
                          >
                            {subMenu.subMenuItems.map((subItem, siIdx) =>
                              subItem.isMegaMenu === false && subItem.link
                                ? renderItem(subItem, siIdx)
                                : renderMegaSubMenuItem(subItem, siIdx)
                            )}
                          </ul>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </nav>
  );
};

export default Navbar;
